import { useSupportContext } from "@/stores";
import { Message } from "@/types";
import { formatDate, LocalStorageService } from "@/utils";
import { FC, Fragment, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useGetChatMessages } from "../../queries";
import { ChatDateSeparator } from "../chat-date-separator";
import { ChatMessage } from "../chat-message/chat-message.component";
import { useNotify } from "react-admin";
import { Box, CircularProgress } from "@mui/material";
import { ChatsApi } from "@/api/chats";

const showDateSeparator = (
  msgIndex: number,
  createdAt: Message["createdAt"],
  previousMessage: Message,
  messagesCount: number
) => {
  return (
    msgIndex === messagesCount - 1 ||
    formatDate(createdAt, "MMM DD, YYYY") !==
      formatDate(previousMessage?.createdAt, "MMM DD, YYYY")
  );
};

const ChatMessages: FC = () => {
  const notify = useNotify();
  const { currentChat } = useSupportContext();
  const {
    messages,
    isChatMessagesLoading,
    chatMessagesError,
    fetchNextChatMessagesPage,
  } = useGetChatMessages({
    chatId: currentChat?.isActive ? currentChat?.id : "",
  });

  useEffect(() => {
    if (chatMessagesError) {
      notify("Error while fetching new feedbacks", { type: "error" });
    }
  }, [chatMessagesError]);

  if (!currentChat) return;

  return (
    <InfiniteScroll
      pullDownToRefresh
      inverse
      height={"100%"}
      dataLength={1}
      hasMore={true}
      loader={<></>}
      style={{
        height: "100%",
        overflow: "auto",
        display: "flex",
        rowGap: 8,
        padding: "16px 0 4px",
        flexDirection: "column-reverse",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
      next={fetchNextChatMessagesPage}
      refreshFunction={() => null}
      scrollThreshold={0.6}
      pullDownToRefreshThreshold={400}
    >
      {currentChat.isActive && isChatMessagesLoading && (
        <Box display="flex" justifyContent="center" pb={4}>
          <CircularProgress />
        </Box>
      )}

      {!currentChat.isActive && currentChat.feedback && (
        <>
          <ChatMessage
            position="start"
            type="feedback"
            read={false}
            id={currentChat.id}
            createdAt={currentChat.feedback.createdAt}
            feedback={currentChat.feedback}
            message={currentChat.feedback.body}
          />
          <ChatDateSeparator date={currentChat.feedback.createdAt} />
        </>
      )}

      {currentChat.isActive &&
        messages?.data &&
        messages.data.map(({ id, createdAt, content, sender, isRead }, idx) => {
          const isMyMessage =
            sender.id === LocalStorageService.getSupportUserId();

          const showDate = showDateSeparator(
            idx,
            createdAt,
            messages.data[idx - 1],
            messages.data.length
          );

          return (
            <Fragment key={id}>
              <ChatMessage
                id={id}
                read={isRead}
                type={content.type}
                createdAt={createdAt}
                feedback={content?.feedback}
                position={isMyMessage ? "end" : "start"}
                attachment={content.attachments?.[0]}
                message={
                  content.type === "text"
                    ? content.body
                    : content.feedback?.body
                }
                onVisible={async () => {
                  if (!isMyMessage && !isRead) {
                    await ChatsApi.readMessage({ messageId: id });
                  }
                }}
              />
              {showDate && <ChatDateSeparator date={createdAt} />}
            </Fragment>
          );
        })}
    </InfiniteScroll>
  );
};

export { ChatMessages };
